import { useMemo } from 'react';
import { visibleOptionLabel } from '@/lib/catalog/optionLabel';
import { useTranslation } from '@/lib/i18n';
import { Button, DataTable, Skeleton, type DataTableColumn } from '@/components/ui';
import { useUserCartQuery, type UserCartItem } from '../hooks/useUserCartQuery';

interface UserCartPanelProps {
  userId: string;
}

const formatKrw = (amount: number): string => `${amount.toLocaleString('ko-KR')}원`;

const formatOptions = (item: UserCartItem, noOption: string): string => {
  if (item.selectedOptions.length === 0) {
    const parts = [visibleOptionLabel(item.variantLabel)].filter((v): v is string => Boolean(v));
    return parts.length > 0 ? parts.join(' / ') : noOption;
  }
  return item.selectedOptions
    .map((opt) => {
      const display = opt.label ?? opt.value ?? '';
      return opt.optionName ? `${opt.optionName}: ${display}` : display;
    })
    .filter(Boolean)
    .join(' / ');
};

export const UserCartPanel = ({ userId }: UserCartPanelProps) => {
  const { t } = useTranslation('common');
  const query = useUserCartQuery(userId);

  const columns = useMemo<DataTableColumn<UserCartItem>[]>(
    () => [
      {
        key: 'product',
        header: t('users.cart.cols.product'),
        cell: (row) => (
          <span style={{ color: 'var(--content-strong)', fontWeight: 500 }}>
            {row.productName ?? row.productId}
          </span>
        ),
      },
      {
        key: 'quantity',
        header: t('users.cart.cols.quantity'),
        align: 'right',
        cell: (row) => row.quantity.toLocaleString('ko-KR'),
      },
      {
        key: 'unitPrice',
        header: t('users.cart.cols.unitPrice'),
        align: 'right',
        cell: (row) => formatKrw(row.unitPrice.amount),
      },
      {
        key: 'lineTotal',
        header: t('users.cart.cols.lineTotal'),
        align: 'right',
        cell: (row) => (
          <span style={{ color: 'var(--content-strong)', fontWeight: 600 }}>
            {formatKrw(row.lineTotal.amount)}
          </span>
        ),
      },
      {
        key: 'addedAt',
        header: t('users.cart.cols.addedAt'),
        cell: (row) => (
          <span style={{ color: 'var(--content-muted)', fontSize: 12 }}>
            {new Date(row.addedAt).toLocaleDateString('ko-KR')}
          </span>
        ),
      },
      {
        key: 'options',
        header: t('users.cart.cols.options'),
        cell: (row) => (
          <span style={{ color: 'var(--content-muted)' }}>
            {formatOptions(row, t('users.cart.noOption'))}
          </span>
        ),
      },
    ],
    [t],
  );

  if (query.isPending) {
    return (
      <div className="space-y-2" role="status" aria-live="polite">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-[44px] w-full" />
        ))}
      </div>
    );
  }

  if (query.isError) {
    return (
      <div
        role="alert"
        className="flex flex-col items-center gap-2 rounded-xl py-10 text-center text-sm"
        style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
      >
        <p>{t('users.cart.loadFailed')}</p>
        <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
          {t('users.actions.retry')}
        </Button>
      </div>
    );
  }

  return (
    <DataTable<UserCartItem>
      columns={columns}
      rows={[...query.data.items]}
      rowKey={(row) => row.cartItemId}
      emptyState={t('users.cart.empty')}
      ariaLabel={t('users.tabs.cart')}
      mobileCardCollapse
    />
  );
};
